import { supabase } from "../../config/supabaseClient";
import { sendWelcomeEmailToSubscriber, hasEmailTemplate } from "./EmailService";
import { shouldShowToViewer } from "../HelperFunction/TimezoneDates";

export type CampaignPageStatus = "active" | "draft" | "archive" | "not-live";

export const CAMPAIGN_PAGE_STATUS_LABELS: Record<CampaignPageStatus, string> = {
  active: "Active",
  draft: "Draft",
  archive: "Archive",
  "not-live": "Not Live",
};

function parseCampaignIdFromComponentDetails(details: unknown): string | null {
  if (!details) return null;
  try {
    const parsed =
      typeof details === "string" ? JSON.parse(details) : details;
    const campaignId = (parsed as { campaign_id?: unknown })?.campaign_id;
    return campaignId != null ? String(campaignId) : null;
  } catch {
    return null;
  }
}

function normalizeCampaignComponentStatus(
  component: any
): "active" | "draft" | "archive" {
  const status = String(component?.status_expiry || component?.status || "active")
    .toLowerCase()
    .trim();

  if (status === "archive") return "archive";
  if (status === "draft") return "draft";
  return "active";
}

/** Resolve link-in-bio page status for a campaign (matches component status_expiry). */
export function getCampaignPageStatus(
  campaignId: string | number | null | undefined,
  pageComponents: any[] | Record<string, any> | null | undefined
): CampaignPageStatus {
  const normalizedId = String(campaignId ?? "").trim();
  if (!normalizedId || !pageComponents) return "not-live";

  const rows = Array.isArray(pageComponents)
    ? pageComponents
    : Object.values(pageComponents);
  const matches = rows.filter(
    (row) => parseCampaignIdFromComponentDetails(row?.details) === normalizedId,
  );
  if (!matches.length) return "not-live";

  const rank: Record<"active" | "draft" | "archive", number> = {
    active: 3,
    draft: 2,
    archive: 1,
  };
  let best: "active" | "draft" | "archive" = normalizeCampaignComponentStatus(
    matches[0],
  );
  for (const row of matches.slice(1)) {
    const status = normalizeCampaignComponentStatus(row);
    if (rank[status] > rank[best]) best = status;
  }
  return best;
}

export function countCampaignsByPageStatus(
  campaigns: Array<{ id?: string | number | null }>,
  pageComponents: any[] | null | undefined
): Record<CampaignPageStatus, number> {
  const counts: Record<CampaignPageStatus, number> = {
    active: 0,
    draft: 0,
    archive: 0,
    "not-live": 0,
  };

  if (!Array.isArray(campaigns)) return counts;

  for (const campaign of campaigns) {
    const status = getCampaignPageStatus(campaign.id, pageComponents);
    counts[status] += 1;
  }

  return counts;
}

export function getCampaignPageStatusLabel(status: CampaignPageStatus): string {
  return CAMPAIGN_PAGE_STATUS_LABELS[status] || CAMPAIGN_PAGE_STATUS_LABELS["not-live"];
}

export function getCampaignPageStatusClassName(status: CampaignPageStatus): string {
  switch (status) {
    case "active":
      return "campaign-status-badge campaign-status-badge--active";
    case "draft":
      return "campaign-status-badge campaign-status-badge--draft";
    case "archive":
      return "campaign-status-badge campaign-status-badge--archive";
    case "not-live":
    default:
      return "campaign-status-badge campaign-status-badge--not-live";
  }
}

/** Campaign IDs whose link-in-bio component is currently visible (not archived/draft). */
export function getLiveCampaignIdsFromComponents(components: any[]): string[] {
  if (!Array.isArray(components)) return [];

  return components
    .filter((component) => shouldShowToViewer(component))
    .map((component) => parseCampaignIdFromComponentDetails(component.details))
    .filter((id): id is string => Boolean(id));
}

export function isCampaignIdLive(
  campaignId: string | number | null | undefined,
  liveCampaignIds: Array<string | number | null | undefined>
): boolean {
  const normalizedId = String(campaignId ?? "").trim();
  if (!normalizedId) return false;

  return liveCampaignIds.some(
    (liveId) => String(liveId ?? "").trim() === normalizedId
  );
}

export function notifyCampaignsUpdated(userId: string) {
  if (typeof window === "undefined" || !userId) return;

  try {
    window.dispatchEvent(
      new CustomEvent("sb:campaigns-updated", { detail: { user_id: userId } })
    );
  } catch {}

  try {
    const bc = new BroadcastChannel("sb:campaigns");
    bc.postMessage({ type: "campaigns-updated", user_id: userId });
    bc.close();
  } catch {}
}

export const storeCampaignEmailTemplate = async (
  campaign: any,
  subject: string,
  body: string
) => {
  const { data, error } = await supabase
    .from("creators_campaigns")
    .update({
      email_subject: subject,
      email_body: body,
    })
    .eq("id", campaign.id)
    .eq("user_id", campaign.user_id);
  if (error) {
   
  } else {
  }
};

/**
 * Add subscriber to campaign and automatically send welcome email if template exists
 */
export const addSubscriberToCampaign = async (
  campaignId: string,
  subscriberData: any,
  username?: string
) => {
  try {
    // Add subscriber to database
    const { data: newSubscriber, error } = await supabase
      .from("campaign_subscriber")
      .insert({
        user_id: subscriberData.user_id,
        campaign_id: campaignId,
        campaign_name: subscriberData.campaign_name,
        details: subscriberData.email,
        other_details: JSON.stringify(subscriberData.fields || []),
      })
      .select();

    if (error) {
      throw new Error(error.message);
    }

    if (newSubscriber && newSubscriber[0]) {
      // Check if campaign has email template
      const hasTemplate = await hasEmailTemplate(campaignId);
      
      if (hasTemplate) {
        // Get campaign data
        const { data: campaign } = await supabase
          .from("creators_campaigns")
          .select("*")
          .eq("id", campaignId)
          .single();

        if (campaign) {
          const welcomeSubject =
            campaign.welcome_email_subject || campaign.email_subject || "";
          const welcomeBody =
            campaign.welcome_email_body || campaign.email_body || "";

          // Send welcome email automatically
          const emailResult = await sendWelcomeEmailToSubscriber(
            newSubscriber[0],
            {
              ...campaign,
              email_subject: welcomeSubject,
              email_body: welcomeBody,
            },
            username
          );

          const emailHistoryData: Record<string, string> = {
            subject: welcomeSubject,
            body: welcomeBody,
            sent_at: new Date().toISOString(),
            type: "welcome",
          };
          if (!emailResult.success) {
            emailHistoryData.status = "failed";
            emailHistoryData.error = emailResult.message || "send failed";
          }

          await supabase
            .from("campaign_subscriber")
            .update({
              email_sent: emailResult.success,
              email_type: "welcome",
              email_history: JSON.stringify(emailHistoryData),
            })
            .eq("id", newSubscriber[0].id);

          return {
            success: true,
            subscriber: newSubscriber[0],
            emailSent: emailResult.success,
          };
        }
      }

      return {
        success: true,
        subscriber: newSubscriber[0],
        emailSent: false,
      };
    }

    return {
      success: false,
      message: "Failed to add subscriber",
    };
  } catch (error: any) {
    console.error("Error adding subscriber to campaign:", error);
    return {
      success: false,
      message: error.message,
    };
  }
};

export const sendEmailToSubscribers = async (campaign: any) => {

  // call mail api from here
  
//   const { data, error } = await supabase
//     .from("creators_campaigns")
//     .update({
//       email_subject: subject,
//       email_body: body,
//     })
//     .eq("id", campaign.id)
//     .eq("user_id", campaign.user_id);
}